import { dateOnlyInTz } from './timezone';

/** Calendar eligibility only: never authorises administration or chooses a dose time. */
export type MedicationCalendarAssessment =
  | { state: 'eligible' }
  | { state: 'off_day' }
  | { state: 'outside_prescription' }
  | {
      state: 'unconfirmed';
      reason: 'invalid_dates' | 'missing_anchor' | 'monthly_policy' | 'frequency';
    };

export interface MedicationCalendarInput {
  frequency: string | null | undefined;
  startDate?: string | null;
  endDate?: string | null;
  /** Explicit confirmed prescription anchor; never inferred from startDate or the viewed day. */
  recurrenceAnchor?: string | null;
  /** No implicit clinical choice when an anchor day does not exist in a month. */
  monthlyPolicy?: 'skip' | 'last_day' | null;
}

function calendarDay(value: string): number | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime()) || dateOnlyInTz(date, 'UTC') !== value) return null;
  return date.getTime() / 86_400_000;
}

/**
 * Compare dates already projected into the governing care-location calendar.
 * UTC here is solely a date-key ordinal, not an event instant or elapsed-day rule.
 * DST, the host timezone and the first date viewed cannot move an occurrence.
 */
export function assessMedicationCalendar(
  medication: MedicationCalendarInput,
  localDate: string,
): MedicationCalendarAssessment {
  if (!medication || typeof medication !== 'object' || Array.isArray(medication))
    return { state: 'unconfirmed', reason: 'invalid_dates' };
  const selected = calendarDay(localDate);
  const start = medication.startDate == null ? null : calendarDay(medication.startDate);
  const end = medication.endDate == null ? null : calendarDay(medication.endDate);
  if (
    selected === null ||
    (medication.startDate != null && start === null) ||
    (medication.endDate != null && end === null) ||
    (start !== null && end !== null && start > end)
  )
    return { state: 'unconfirmed', reason: 'invalid_dates' };
  if ((start !== null && selected < start) || (end !== null && selected > end))
    return { state: 'outside_prescription' };

  if (medication.frequency != null && typeof medication.frequency !== 'string')
    return { state: 'unconfirmed', reason: 'frequency' };
  const frequency = medication.frequency?.trim().toLowerCase();
  if (
    [
      'once_daily',
      'twice_daily',
      'three_times_daily',
      'four_times_daily',
      'daily',
      'bd',
      'tds',
      'qid',
      'mane',
      'nocte',
    ].includes(frequency ?? '')
  )
    return { state: 'eligible' };
  // PRN/STAT have no recurring calendar occurrence. Recording remains a separate path.
  if (['as_needed', 'prn', 'stat'].includes(frequency ?? '')) return { state: 'off_day' };
  if (!['weekly', 'fortnightly', 'monthly'].includes(frequency ?? ''))
    return { state: 'unconfirmed', reason: 'frequency' };
  const anchor =
    medication.recurrenceAnchor == null ? null : calendarDay(medication.recurrenceAnchor);
  if (anchor === null) return { state: 'unconfirmed', reason: 'missing_anchor' };
  if (frequency === 'monthly' && !['skip', 'last_day'].includes(medication.monthlyPolicy ?? ''))
    return { state: 'unconfirmed', reason: 'monthly_policy' };
  if (selected < anchor) return { state: 'off_day' };
  if (frequency !== 'monthly')
    return {
      state: (selected - anchor) % (frequency === 'weekly' ? 7 : 14) === 0 ? 'eligible' : 'off_day',
    };
  const anchorDay = Number(medication.recurrenceAnchor!.slice(8, 10));
  const last = new Date(`${localDate.slice(0, 7)}-01T00:00:00.000Z`);
  last.setUTCMonth(last.getUTCMonth() + 1, 0);
  const target =
    medication.monthlyPolicy === 'last_day' ? Math.min(anchorDay, last.getUTCDate()) : anchorDay;
  return { state: Number(localDate.slice(8, 10)) === target ? 'eligible' : 'off_day' };
}
